import React from 'react'
import { Card, Tooltip, Typography } from 'antd'
import { BarChartOutlined, WarningOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'

const { Title } = Typography

interface QuickStatsProps {
  stats: {
    totalRequestsThisYear: number
    averageLeaveLength: number
    approvedRequests: number
    pendingRequests: number
    approverName?: string | null
    averageApprovalTime?: string | null
    nextAccrual?: string | null
    capHit?: boolean
    leaveTypeName?: string | null
  }
  isLoading?: boolean
  className?: string
}

const QuickStatsComponent: React.FC<QuickStatsProps> = ({
  stats,
  className = '',
}) => {
  const { formatMessage } = useIntl()

  return (
    <Card 
      className={`quick-stats ${className}`}
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <BarChartOutlined style={{ marginRight: 8 }} />
          <Title level={4} style={{ margin: 0 }}>
            {formatMessage({ id: 'dashboard.stats.title' })}
          </Title>
        </div>
      }
    >
      <div className="stats-container">
        <div className="stat-row">
          <span className="stat-label">
            {formatMessage({ id: 'dashboard.stats.requestsThisYear' })}
          </span>
          <span className="stat-value">
            {formatMessage(
              { id: 'dashboard.stats.requestsCount' },
              { count: stats.totalRequestsThisYear }
            )}
          </span>
        </div>
        
        <div className="stat-row">
          <span className="stat-label">
            {formatMessage({ 
              id: stats.approverName?.includes(',') 
                ? 'dashboard.stats.yourApprovers' 
                : 'dashboard.stats.yourApprover', 
            })}
          </span>
          <span className={`stat-value ${!stats.approverName ? 'not-assigned' : ''}`}>
            {stats.approverName || formatMessage({ id: 'dashboard.stats.notAssigned' })}
          </span>
        </div>
        
        {stats.nextAccrual && (
          <div className="stat-row">
            <span className="stat-label">
              {formatMessage({ id: 'dashboard.stats.nextAccrual' })}
            </span>
            <span className="stat-value">
              {
                stats.capHit ? (
                  <span className="cap-hit">
                    <Tooltip title={formatMessage(
                      { id: 'components.leavePolicyForm.accrualCapWarning' },
                      { leaveTypeName: stats.leaveTypeName }
                    )}>
                      <WarningOutlined /> <IntlMessages id="app.accrualCapHit" />
                    </Tooltip>
                  </span>
                ) : (
                  stats.nextAccrual
                )
              }
            </span>
          </div>
        )}
      </div>
    </Card>
  )
}

// Memoize component to prevent unnecessary re-renders
// Only re-render when stats data changes
const QuickStats = React.memo(QuickStatsComponent, (prevProps, nextProps) => {
  // Custom comparison function - return true if props are equal (skip re-render)
  // For stats object, we'll do a shallow comparison of its properties
  const prevStats = prevProps.stats
  const nextStats = nextProps.stats
  
  return (
    prevStats.totalRequestsThisYear === nextStats.totalRequestsThisYear &&
    prevStats.averageLeaveLength === nextStats.averageLeaveLength &&
    prevStats.approvedRequests === nextStats.approvedRequests &&
    prevStats.pendingRequests === nextStats.pendingRequests &&
    prevStats.approverName === nextStats.approverName &&
    prevStats.averageApprovalTime === nextStats.averageApprovalTime &&
    prevStats.nextAccrual === nextStats.nextAccrual &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.className === nextProps.className
  )
})

// Set display name for debugging
QuickStats.displayName = 'QuickStats'

export default QuickStats